/**
 * Stima locale della robustezza di una password (research R-11): quattro livelli calcolati da
 * lunghezza, varietà dei caratteri e sequenze ovvie, senza librerie. È solo un aiuto per chi
 * scrive: il verdetto vincolante (password comuni o violate) resta quello del server.
 */

export type PasswordStrength = 'debole' | 'sufficiente' | 'buona' | 'ottima';

export interface PasswordStrengthEstimate {
  readonly level: PasswordStrength;
  /** Segmenti pieni del misuratore: 0 con la password vuota, poi da 1 (debole) a 4 (ottima). */
  readonly score: 0 | 1 | 2 | 3 | 4;
  /** Lunghezza minima raggiunta. */
  readonly longEnough: boolean;
  /** La password non contiene l'email, il nome o il cognome. */
  readonly avoidsPersonal: boolean;
  /** Bit di entropia stimati, dopo le penalità. */
  readonly bits: number;
}

/** Lunghezza minima predefinita (FR-040); in sviluppo il server può indicarne una più bassa. */
export const PASSWORD_MIN_LENGTH = 12;

/** Soglie in bit fra i livelli, per password che hanno già la lunghezza minima. */
const SUFFICIENT_BITS = 45;
const GOOD_BITS = 65;
const GREAT_BITS = 90;

/** Parole che compaiono spesso nelle password: valgono poco anche se lunghe. */
const COMMON_WORDS = [
  'password',
  'passw0rd',
  'qwerty',
  'qwertz',
  'azerty',
  'asdf',
  'zxcv',
  'admin',
  'letmein',
  'welcome',
  'iloveyou',
  'monkey',
  'dragon',
  'ciao',
  'amore',
  'tiamo',
  'juventus',
  'milan',
  'inter',
  'napoli',
  'roma',
  'lazio',
  'calcio',
  'forza',
  'italia',
  'cantiere',
  'bsite',
  'edilmanager',
];

const KEYBOARD_ROWS = ['qwertyuiop', 'asdfghjkl', 'zxcvbnm', '1234567890'];

const LEVELS: readonly PasswordStrength[] = ['debole', 'sufficiente', 'buona', 'ottima'];

/** Stima la robustezza; `personalValues` sono email, nome e cognome dell'utente. */
export function estimatePasswordStrength(
  password: string,
  personalValues: readonly string[] = [],
  minLength = PASSWORD_MIN_LENGTH,
): PasswordStrengthEstimate {
  const longEnough = [...password].length >= minLength;
  const avoidsPersonal = !containsPersonalValue(password, personalValues);
  if (password.length === 0) {
    return { level: 'debole', score: 0, longEnough, avoidsPersonal, bits: 0 };
  }
  const bits = estimateBits(password);
  const index = longEnough && avoidsPersonal ? levelIndex(bits) : 0;
  return {
    level: LEVELS[index],
    score: (index + 1) as 1 | 2 | 3 | 4,
    longEnough,
    avoidsPersonal,
    bits,
  };
}

function levelIndex(bits: number): number {
  if (bits >= GREAT_BITS) return 3;
  if (bits >= GOOD_BITS) return 2;
  if (bits >= SUFFICIENT_BITS) return 1;
  return 0;
}

/** Entropia stimata: lunghezza "efficace" per il logaritmo dell'alfabeto usato. */
function estimateBits(password: string): number {
  const chars = [...password];
  const lower = password.toLowerCase();

  // Ogni carattere che ripete il precedente o continua una sequenza (abc, 321, qwe) vale un quarto.
  let effective = 0;
  for (let i = 0; i < chars.length; i++) {
    effective += i > 0 && continuesRun(lower, i) ? 0.25 : 1;
  }

  // Un blocco ripetuto (abcabcabc) vale poco più del blocco.
  const unit = repeatedUnit(chars);
  if (unit < chars.length) {
    effective = Math.min(effective, unit + 2);
  }

  // Le parole comuni valgono un quarto della loro lunghezza.
  for (const word of COMMON_WORDS) {
    if (lower.includes(word)) {
      effective -= word.length * 0.75;
    }
  }
  effective = Math.max(effective, 1);

  return effective * Math.log2(alphabetSize(password));
}

/** Lunghezza del blocco più corto che, ripetuto, forma tutta la password. */
function repeatedUnit(chars: readonly string[]): number {
  for (let unit = 1; unit <= chars.length / 2; unit++) {
    if (chars.length % unit === 0 && chars.every((char, i) => char === chars[i % unit])) {
      return unit;
    }
  }
  return chars.length;
}

function continuesRun(lower: string, index: number): boolean {
  const previous = lower.codePointAt(index - 1) ?? 0;
  const current = lower.codePointAt(index) ?? 0;
  if (current === previous || Math.abs(current - previous) === 1) {
    return true;
  }
  const pair = lower.slice(index - 1, index + 1);
  return KEYBOARD_ROWS.some(
    (row) => row.includes(pair) || row.includes([...pair].reverse().join('')),
  );
}

function alphabetSize(password: string): number {
  let size = 0;
  if (/[a-z]/.test(password)) size += 26;
  if (/[A-Z]/.test(password)) size += 26;
  if (/\d/.test(password)) size += 10;
  if (/[^a-zA-Z0-9]/.test(password)) size += 33;
  return Math.max(size, 2);
}

/**
 * La password contiene un dato personale: l'email intera, la sua parte prima della chiocciola o
 * una parola di almeno tre lettere del nome (senza distinguere maiuscole e accenti).
 */
function containsPersonalValue(password: string, personalValues: readonly string[]): boolean {
  const normalizedPassword = normalize(password);
  if (!normalizedPassword) {
    return false;
  }
  const tokens = new Set<string>();
  for (const value of personalValues) {
    const normalized = normalize(value.trim());
    if (!normalized) continue;
    tokens.add(normalized);
    // Dell'email contano la parte prima della chiocciola e le sue parole, non il dominio.
    const local = normalized.split('@')[0];
    tokens.add(local);
    for (const part of local.split(/[^a-z0-9]+/)) {
      tokens.add(part);
    }
  }
  return [...tokens].some((token) => token.length >= 3 && normalizedPassword.includes(token));
}

function normalize(value: string): string {
  return value
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase();
}
